import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import MarkdownIt from 'markdown-it';
import { markdownItTable } from 'markdown-it-table';
import { MarkdownParser } from '@atlaskit/editor-prosemirror/markdown';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { filterMdToPmSchemaMapping, getMdToPmMapping, PM_SCHEMA_TO_MD_MAPPING } from './content-mapping';
import markdownItCaption from './markdown-to-pm/md/caption-md-plugin';
import markdownItCitation from './markdown-to-pm/md/citation-md-plugin';
import markdownItCustomBlock from './markdown-to-pm/md/custom-block-md-plugin';
import markdownItCustomInline from './markdown-to-pm/md/custom-inline-md-plugin';
import markdownItDecisionList from './markdown-to-pm/md/decisionlist-md-plugin';
import markdownItHardbreak from './markdown-to-pm/md/hardbreak-md-plugin';
import markdownItLinkify from './markdown-to-pm/md/linkify-md-plugin';
import { markdownItMedia } from './markdown-to-pm/md/media-md-plugin';
import markdownItNewline from './markdown-to-pm/md/newline-md-plugin';
import markdownItNextMedia from './markdown-to-pm/md/next-media-md-plugin';
import markdownItParagraph from './markdown-to-pm/md/paragraph-md-plugin';
import markdownItTaskList from './markdown-to-pm/md/tasklist-md-plugin';
import { escapeLinks } from './markdown-to-pm/utils/hyperlink';

// Remove once markdownPlusAttributeTest is removed

function getFragmentBackingArray(fragment) {
  // Ignored via go/ees005
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fragment.content;
}
function mapFragment(content, callback) {
  var parent = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var children = [];
  for (var i = 0, size = content.childCount; i < size; i++) {
    var node = content.child(i);
    var transformed = node.isLeaf ? callback(node, parent, i) : callback(node.copy(mapFragment(node.content, callback, node)), parent, i);
    if (transformed) {
      if (transformed instanceof Fragment) {
        children.push.apply(children, _toConsumableArray(getFragmentBackingArray(transformed)));
      } else if (Array.isArray(transformed)) {
        children.push.apply(children, _toConsumableArray(transformed));
      } else {
        children.push(transformed);
      }
    }
  }
  return Fragment.fromArray(children);
}

// WARNING
// If this import changes -- it means we have a breaking change for products
// where currently it's expected they are using older versions of editor-common
/**
 * Converts a markdown string into a Prosemirror document fragment
 *
 * @param markdown - The markdown string to convert
 * @param schema - The Prosemirror schema to use for the conversion
 * @param idMap - The id map to use for the conversion
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @returns The converted Prosemirror document fragment, the number of html nodes converted to Prosemirror nodes and the number of html nodes converted to fallback Prosemirror nodes
 */
export function convertMarkdownToProsemirror(_ref) {
  var markdown = _ref.markdown,
    schema = _ref.schema,
    idMap = _ref.idMap,
    _ref$featureToggles = _ref.featureToggles,
    featureToggles = _ref$featureToggles === void 0 ? {} : _ref$featureToggles;
  var markdownPlus = featureToggles.markdownPlus;
  var tokenizer = new MarkdownIt('zero', {
    html: false
  });
  tokenizer.enable([
  // Process html entity - &#123;, &#xAF;, &quot;, ...
  'entity',
  // Process escaped chars and hardbreaks
  'escape', 'newline']);
  ['nodes', 'marks'].forEach(function (key) {
    for (var idx in PM_SCHEMA_TO_MD_MAPPING[key]) {
      if (schema[key][idx]) {
        tokenizer.enable(PM_SCHEMA_TO_MD_MAPPING[key][idx]);
      }
    }
  });
  var htmlConversionCounts = {
    htmlConvertedToNodes: 0,
    htmlConvertedToFallbackNodes: 0
  };

  // Remove once markdownPlusAttributeTest is removed
  var markdownPlusAttributeStatus = [];
  if (markdownPlus) {
    tokenizer.use(markdownItCustomBlock, idMap, htmlConversionCounts);
    tokenizer.use(markdownItDecisionList, idMap);
    tokenizer.use(markdownItCustomInline, idMap, featureToggles, htmlConversionCounts, markdownPlusAttributeStatus);
  }
  tokenizer.use(markdownItCitation, idMap);
  tokenizer.use(markdownItParagraph);
  // enable modified version of linkify plugin
  // @see https://product-fabric.atlassian.net/browse/ED-3097

  tokenizer.use(markdownItLinkify);
  tokenizer.use(markdownItNewline);
  tokenizer.use(markdownItHardbreak);

  // Custom Table tokenizer
  if (schema.nodes.table) {
    tokenizer.use(markdownItTable);
  }

  // Custom Media tokenizer
  if (schema.nodes.media && schema.nodes.mediaSingle) {
    if (editorExperiment('platform_editor_markdown_next_media_plugin_exp', true, {
      exposure: true
    })) {
      tokenizer.use(markdownItNextMedia);
    } else {
      tokenizer.use(markdownItMedia);
    }
    if (markdownPlus && featureToggles.markdownPlusAddComplexNodes) {
      tokenizer.use(markdownItCaption);
    }
  }

  // Custom TaskList tokenizer
  if (schema.nodes.taskList) {
    tokenizer.use(markdownItTaskList);
  }
  var markdownParser = new MarkdownParser(schema, tokenizer, filterMdToPmSchemaMapping(schema, getMdToPmMapping(!!markdownPlus)));
  if (markdown === '') {
    return {
      htmlConvertedToNodes: 0,
      htmlConvertedToFallbackNodes: 0,
      markdownPlusAttributeStatus: [],
      pmFragment: Fragment.empty
    };
  }

  // normalize duplicated backticks (needed as a safeguard to avoid rendering malformed escape characters coming from backed)
  // the case with newline characters
  // Ignored via go/ees005
  // eslint-disable-next-line require-unicode-regexp
  markdown = markdown.replace(/\\n/g, '\n');
  // the case with custom field attributes (limit only to data- attributes inside custom fields)
  if (markdownPlus) {
    // Ignored via go/ees005
    // eslint-disable-next-line require-unicode-regexp
    markdown = markdown.replace(/<custom[^>]*?>/g, function (match) {
      // Ignored via go/ees005
      // eslint-disable-next-line require-unicode-regexp
      return match.replace(/(data-\w+=)\\\"(.*?)\\\"/g, '$1"$2"');
    });
  }
  var prosemirrorNode = markdownPlus ? markdownParser.parse(markdown) : markdownParser.parse(escapeLinks(markdown));
  if (!prosemirrorNode.content) {
    throw new Error('Markdown conversion did not result in a valid document being created');
  }

  // This logic is duplicated from editor-cores paste transformation.
  // The editor-plugin-ai package is avoiding sharing dependencies with editor-core to support
  // products using it with various versions of editor packages.
  // The duplication is from the following file:
  // packages/editor/editor-core/src/plugins/media/utils/media-single.ts

  // WARNING
  // If the `mapFragment` usage changes -- it means we have a breaking change for products
  // where currently it's expected they are using older versions of editor-common
  var mappedContent = mapFragment(prosemirrorNode.content, function (node) {
    if (node.type.name === 'media') {
      var replacedAttrs = Object.fromEntries(Object.entries(node.attrs).map(function (_ref2) {
        var _ref3 = _slicedToArray(_ref2, 2),
          key = _ref3[0],
          value = _ref3[1];
        // If value is the literal string 'null', convert it to JS null
        return [key, value === 'null' ? null : value];
      }));
      return schema.nodes.media.createChecked(_objectSpread(_objectSpread({}, replacedAttrs), {}, {
        // Adding this attribute results in the media node being rendered
        // initially using the external url, but then triggering an upload
        // of the external url as an atlassian artifact, and this media
        // will then be replaced to use the atlassian artifact.
        //
        // This is done in the media nodeview by checking the attributes
        // when rendering.
        __external: replacedAttrs.type === 'external',
        __mediaTraceId: null
      }), node.content, node.marks);
    }
    return node;
  });
  return _objectSpread(_objectSpread({}, htmlConversionCounts), {}, {
    markdownPlusAttributeStatus: markdownPlusAttributeStatus,
    pmFragment: mappedContent
  });
}

/**
 * Uses the `convertMarkdownToProsemirror` function to convert markdown to a ProseMirror node, instead of a fragment.
 *
 * The node that is returned is a ProseMirror document with the markdown converted to ProseMirror nodes nested in the document.
 *
 * This will throw an error if the fragment returned from `convertMarkdownToProsemirror` cannot be converted into a document node.
 *
 * @param markdown - The markdown string to convert
 * @param schema - The Prosemirror schema to use for the conversion
 * @param idMap - The id map to use for the conversion
 * @param featureToggles - Optional feature toggles to use for the conversion.
 * @returns The converted Prosemirror document node
 */
export function backendConvertMarkdownToProsemirrorNode(_ref4) {
  var markdown = _ref4.markdown,
    schema = _ref4.schema,
    idMap = _ref4.idMap,
    _ref4$featureToggles = _ref4.featureToggles,
    featureToggles = _ref4$featureToggles === void 0 ? {} : _ref4$featureToggles;
  var fragment = convertMarkdownToProsemirror({
    markdown: markdown,
    schema: schema,
    idMap: idMap,
    featureToggles: _objectSpread({
      markdownPlus: true
    }, featureToggles)
  });
  try {
    var node = schema.nodes.doc.createChecked({}, fragment.pmFragment);
    return node;
  } catch (_unused) {
    throw new Error('Markdown conversion did not result in a valid ProseMirror node');
  }
}